/**
 * AudioEye Accessibility Block
 *
 * Renders an AudioEye accessibility statement iframe for the property's
 * AudioEye site. The hash is sourced from the property's
 * `custom_audioeye_site_hash` field (populated at deploy time by
 * `vibesites/src/api/audioeye-bake.js`). Editors can override per-block
 * with a custom value if they need to point at a different AE site.
*
 * Editor preview shows a placeholder card instead of the live iframe —
 * the deployed hostname is what AE binds against, so a real iframe in
 * the editor would render the wrong site's statement (or none).
 */

import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken, IS_IN_EDITOR_FRAME } from '@vibesites/runtime/react';

const STATEMENT_BASE = 'https://accessibility-statement.audioeye-services.com/';

function AudioeyeAccessibilityPlaceholder({ reason }: { reason: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#ffffff',
        padding: '48px 16px',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '800px',
          border: '2px dashed #d1d5db',
          borderRadius: 8,
          padding: '32px 24px',
          color: '#4b5563',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          textAlign: 'center',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 8 }}>AudioEye Accessibility Statement</div>
        <div style={{ fontSize: 14 }}>{reason}</div>
      </div>
    </div>
  );
}

function AudioeyeAccessibilityComponent(_props: BlockProps) {
  const siteHash = useBlockToken('site_hash', {
    defaultField: 'custom_audioeye_site_hash',
  });

  if (IS_IN_EDITOR_FRAME) {
    return (
      <AudioeyeAccessibilityPlaceholder
        reason="The live accessibility statement will appear here on the deployed site."
      />
    );
  }

  if (!siteHash) {
    return (
      <AudioeyeAccessibilityPlaceholder
        reason="Accessibility statement not yet available — AudioEye provisioning will populate it on the next deploy."
      />
    );
  }

  const src = `${STATEMENT_BASE}?siteHash=${encodeURIComponent(siteHash)}`;

  return (
    <div style={{ display: 'flex', justifyContent: 'center', backgroundColor: '#ffffff' }}>
      <div style={{ width: '100%', maxWidth: '800px' }}>
        <iframe
          src={src}
          title="Accessibility Statement"
          style={{ width: '100%', height: '2300px', border: 'none', display: 'block' }}
        />
      </div>
    </div>
  );
}

const contentSchema: ComponentContentSchema = {
  slot: 'AudioeyeAccessibility',
  displayName: 'AudioEye Accessibility',
  tokens: {
    site_hash: {
      label: 'Site Hash',
      type: 'text',
      defaultField: 'custom_audioeye_site_hash',
      suggestedFields: ['custom_audioeye_site_hash'],
      description:
        'AudioEye site hash. Auto-populated at deploy time from `custom_audioeye_site_hash`; override only to point this block at a different AE site.',
    },
  },
};

export const audioeyeAccessibilityBlock: BlockDefinition = {
  id: 'audioeye-accessibility',
  name: 'AudioEye Accessibility',
  description:
    'Embeds an AudioEye accessibility statement iframe for the property. Site hash is auto-provisioned at deploy time.',
  category: 'Integrations',
  component: AudioeyeAccessibilityComponent,
  contentSchema,
  tags: ['audioeye', 'accessibility', 'ada', 'compliance'],
  defaultGridSpan: 12,
};
